import type { TaskType } from '@/types'

export interface GuideStep {
  title: string
  description: string
}

export interface GuideContent {
  title: string
  description: string
  accentVar: string
  highlights: string[]
  steps: GuideStep[]
  tips?: string[]
}

export interface FeatureMapEntry {
  route: string
  featureKey: string
  taskType: TaskType
}

export const FEATURE_MAP: FeatureMapEntry[] = [
  { route: '/generate/image/competitor', featureKey: 'competitor-research', taskType: 'competitor_research' },
  { route: '/generate/image/variation', featureKey: 'variation', taskType: 'variation' },
  { route: '/generate/image/freestyle', featureKey: 'image-freestyle', taskType: 'image_freestyle' },
  { route: '/generate/video/digital-human', featureKey: 'digital-human', taskType: 'digital_human' },
  { route: '/generate/video/freestyle', featureKey: 'video-freestyle', taskType: 'video_freestyle' },
  // Creative Lab
  { route: '/creative-lab/ideas', featureKey: 'ideas', taskType: 'idea_research' },
  { route: '/creative-lab/vertical-ideas', featureKey: 'vertical-ideas', taskType: 'vertical_ideas' },
  { route: '/creative-lab/foreplay', featureKey: 'foreplay-research', taskType: 'foreplay_search' },
  { route: '/creative-lab/perplexity', featureKey: 'perplexity-research', taskType: 'perplexity_search' },
  // Drama Mix
  { route: '/drama', featureKey: 'drama-mix', taskType: 'short_drama_mix' },
]

export function getFeatureByRoute(pathname: string): FeatureMapEntry | null {
  return FEATURE_MAP.find((entry) => pathname === entry.route || pathname.startsWith(entry.route)) || null
}

export const GUIDE_CONTENT: Record<string, GuideContent> = {
  'competitor-research': {
    title: 'Competitor Research',
    description: 'Find strong competitor ads, rank them by fit, and turn selected references into new creatives.',
    accentVar: '--spectrum-teal',
    highlights: [
      'Search ads by keyword or brand',
      'AI ranks references by relevance to your product',
      'Generate original creatives from selected ads',
      'More references usually means more variety',
    ],
    steps: [
      { title: 'Select Product', description: 'Choose an existing product or paste a URL' },
      { title: 'Refine Search', description: 'Adjust keywords and competitor brands' },
      { title: 'Review Results', description: 'Check scored ads and pick references' },
      { title: 'Generate', description: 'Create new creatives from your selections' },
    ],
    tips: [
      'Each selected reference generates 1 image',
      'Mix different ad styles for broader testing',
      'Add competitor brands to find niche-specific examples',
    ],
  },

  'variation': {
    title: 'Creative Variation',
    description: 'Turn one strong creative into multiple new directions without starting over.',
    accentVar: '--spectrum-cyan',
    highlights: [
      'AI reads style, scene, color, and mood',
      'Get tailored variation directions for your image',
      'Test composition, audience, and creative angle',
      'Keep the product while changing the presentation',
    ],
    steps: [
      { title: 'Select Image', description: 'Pick any image from your library as the source' },
      { title: 'Review Ideas', description: 'Check the suggested variation directions' },
      { title: 'Choose', description: 'Select the directions you want to test' },
      { title: 'Generate', description: 'Create new versions of the original creative' },
    ],
    tips: [
      'Add product info for more relevant output',
      'Use "Your Preferences" to guide style',
      'Pick several directions for quick A/B testing',
    ],
  },

  'image-freestyle': {
    title: 'Image Freestyle',
    description: 'Generate ad images from scratch with a prompt, model choice, and optional product or reference context.',
    accentVar: '--spectrum-blue',
    highlights: [
      'Nano Banana Pro for fidelity, GPT Image 2 for creativity',
      'Reference images help steer style and composition',
      'Product screenshots can keep outputs on-brand',
      'Use Ideas to fill slots for fast batch testing',
      'Supports square, wide, and tall formats',
    ],
    steps: [
      { title: 'Set Context', description: 'Link a product and decide whether to use Ideas' },
      { title: 'Describe', description: 'Write a prompt or let each idea guide a slot' },
      { title: 'Configure', description: 'Choose model, aspect ratio, and image count' },
      { title: 'Generate', description: 'Create images per slot, then iterate or save' },
    ],
    tips: [
      'Use Ideas for quick A/B batches',
      'Try both models on the same prompt',
      'Reference images help with consistency',
    ],
  },

  'video-freestyle': {
    title: 'Video Freestyle',
    description: 'Create short-form ad videos with a prompt, model choice, and optional idea or reference guidance.',
    accentVar: '--spectrum-purple',
    highlights: [
      'Choose Seedance or Veo 3.1',
      'Use Ideas to fill slots automatically',
      'Reference images help anchor the first frame',
      'Built for vertical and widescreen placements',
    ],
    steps: [
      { title: 'Set Context', description: 'Link a product and decide whether to use Ideas' },
      { title: 'Describe', description: 'Write a prompt or let each idea guide a slot' },
      { title: 'Configure', description: 'Choose model, duration, and aspect ratio' },
      { title: 'Generate', description: 'Create videos per slot and review the results' },
    ],
    tips: [
      'Start with 4s to test the concept first',
      'Seedance is usually the lowest-cost option',
      'Reference images improve consistency',
    ],
  },

  'digital-human': {
    title: 'Digital Human',
    description: 'Create spokesperson-style ads with an AI-written script, avatar, voice, and final video in one flow.',
    accentVar: '--spectrum-magenta',
    highlights: [
      'AI handles script, avatar, voice, and rendering',
      'Complete Mode adds intro and CTA segments',
      'Product details drive the script',
      'Optional burned-in subtitles for social delivery',
    ],
    steps: [
      { title: 'Select Product', description: 'Choose the product the video will promote' },
      { title: 'Configure', description: 'Pick aspect ratio, video mode, and subtitle options' },
      { title: 'Add Hints', description: 'Provide talking points or leave it to AI' },
      { title: 'Generate', description: 'Create the full spokesperson video' },
    ],
    tips: [
      'More product detail usually means a better script',
      'Complete Mode is best for full ad structure',
      'Enable subtitles for muted autoplay',
    ],
  },

  // Creative Lab guides

  'ideas': {
    title: 'Ideas',
    description: 'Store and organize creative ideas from research, then send them into Image or Video Freestyle.',
    accentVar: '--spectrum-lime',
    highlights: [
      'Collect ideas from ad, web, and deep research',
      'Use ideas directly as freestyle slots',
      'Tag, favorite, and filter your best concepts',
      'Track what is used and what is still fresh',
    ],
    steps: [
      { title: 'Research', description: 'Use research tools to find promising angles' },
      { title: 'Save', description: 'Turn ads or insights into ideas' },
      { title: 'Organize', description: 'Tag, favorite, and filter the list' },
      { title: 'Generate', description: 'Use Ideas in Freestyle to create ads' },
    ],
    tips: [
      'Generate Ideas can fill the board quickly',
      'Deep Research is slower but more strategic',
      'Star your strongest ideas for reuse',
    ],
  },

  'vertical-ideas': {
    title: 'Vertical Ideas',
    description: 'Generate ideas with a workflow tailored to a product type, starting with novels.',
    accentVar: '--spectrum-emerald',
    highlights: [
      'Pick a supported vertical before you start',
      'Create or reuse a product for that vertical',
      'Novel ideas are generated directly from parsed text',
      'Output works directly in Freestyle',
    ],
    steps: [
      { title: 'Choose Type', description: 'Select a supported vertical such as Novel' },
      { title: 'Choose Product', description: 'Use an existing product or create a new one' },
      { title: 'Parse', description: 'Extract product info and raw novel text' },
      { title: 'Generate', description: 'Create new image and video ideas' },
    ],
    tips: [
      'Novel products need raw text in metadata',
      'One run can produce both image and video ideas',
    ],
  },

  'foreplay-research': {
    title: 'Ad Research',
    description: 'Search real ads by keyword or brand to study what is working and save the best examples.',
    accentVar: '--spectrum-spring',
    highlights: [
      'Search ads across major social platforms',
      'Filter by format, platform, and run time',
      'Use brand views to study competitors',
      'Save standout ads as ideas',
    ],
    steps: [
      { title: 'Search', description: 'Enter keywords or switch to brand mode' },
      { title: 'Filter', description: 'Narrow results to the right set' },
      { title: 'Review', description: 'Study ads and spot patterns' },
      { title: 'Save', description: 'Add strong examples to Ideas' },
    ],
    tips: [
      'Use "Longest Running" to find proven ads',
      'Brand Research is useful for competitor study',
      'Save good examples for later generation',
    ],
  },

  'perplexity-research': {
    title: 'Web Research',
    description: 'Use web search and AI chat to research trends, audiences, and creative angles with citations.',
    accentVar: '--spectrum-emerald',
    highlights: [
      'Search mode for facts, chat mode for analysis',
      'Citations make results easy to verify',
      'Pull images and videos for inspiration',
      'Country and language filters help local research',
    ],
    steps: [
      { title: 'Choose Mode', description: 'Use Search or AI Chat' },
      { title: 'Ask', description: 'Query trends, audiences, or angles' },
      { title: 'Review', description: 'Check answers, citations, and media' },
      { title: 'Save', description: 'Turn useful findings into ideas' },
    ],
    tips: [
      'Ask about trends and audience pain points',
      'Use region filters for local market research',
      'AI Chat works well for angle and copy brainstorming',
    ],
  },

  'drama-mix': {
    title: 'Drama Mix',
    description: 'Turn a full multi-episode short-drama into structured clips, then mix the best moments into promo videos.',
    accentVar: '--spectrum-magenta',
    highlights: [
      'Upload episodes — AI transcribes, summarizes, and finds characters',
      'Auto-cut into searchable segments scored by hook & conflict',
      'Generate several selection plans, then fine-tune in the workbench',
      'Speed, transitions, captions, BGM, intro/outro — every export is kept',
    ],
    steps: [
      { title: 'Upload Episodes', description: 'Add the drama files and run preprocessing' },
      { title: 'Generate Plans', description: 'Pick how many selection plans the AI should propose' },
      { title: 'Polish', description: 'Open a render and tune clips, transitions, captions, and BGM' },
      { title: 'Export', description: 'Render the final cut — each export is saved independently' },
    ],
    tips: [
      'Preprocessing runs ASR + scene analysis; give it a few minutes per episode',
      'Each plan becomes its own render — make several versions to A/B test',
      'Re-exporting keeps every cut; remove unwanted ones from the 成片 tab',
    ],
  },
}
